import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { VerboseErrorHandler } from './core/verbose-error-handler';
import { provideRouter, withPreloading, withRouterConfig } from '@angular/router';
import { IdlePreloading } from './core/idle-preloading';
import { provideHttpClient } from '@angular/common/http';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
  connectAuthEmulator,
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  provideAuth,
} from '@angular/fire/auth';
import {
  CACHE_SIZE_UNLIMITED,
  connectFirestoreEmulator,
  provideFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from '@angular/fire/firestore';
import { getApp } from '@angular/fire/app';
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import { connectFunctionsEmulator, getFunctions, provideFunctions } from '@angular/fire/functions';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { Capacitor } from '@capacitor/core';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { PrivacyService } from './core/services/privacy';

if (!environment.production) {
  console.log(
    `[TranspaEvent] Environment: ${environment.name} | Emulators: ${environment.useEmulators}`
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: VerboseErrorHandler },
    provideRouter(
      routes,
      // Toutes les routes sont préchargées — l'application doit rester
      // utilisable hors ligne — mais seulement quand le fil principal a rendu
      // la main (voir `IdlePreloading`).
      withPreloading(IdlePreloading),
      withRouterConfig({ paramsInheritanceStrategy: 'always' })
    ),
    provideHttpClient(),

    // Le mode « montants masqués » doit être connu AVANT le premier rendu :
    // sinon l'app s'ouvre en affichant les vrais montants le temps de lire la
    // préférence. La lecture est locale et immédiate.
    provideAppInitializer(() => inject(PrivacyService).ready),

    provideFirebaseApp(() => initializeApp(environment.firebase)),

    provideAuth(() => {
      // In the native WKWebView/Android WebView (capacitor://localhost), the default
      // getAuth() wires up `browserPopupRedirectResolver`, whose iframe/popup path
      // fails with an opaque "Script error" — it stops onAuthStateChanged from firing
      // AND makes signInWithCredential hang. Initialize Auth explicitly with IndexedDB
      // persistence and NO popup resolver on native (we never use web popups there).
      const auth = Capacitor.isNativePlatform()
        ? initializeAuth(getApp(), { persistence: indexedDBLocalPersistence })
        : getAuth();
      if (environment.useEmulators && environment.emulators) {
        connectAuthEmulator(auth, environment.emulators.authUrl, { disableWarnings: true });
      }
      return auth;
    }),

    provideFirestore(() => {
      // Le cache persistant est monté DANS LES DEUX CAS. La branche émulateur
      // retournait auparavant une instance nue : le comportement hors ligne
      // n'était donc pas reproductible en développement, alors que c'est
      // exactement ce qu'il faut pouvoir éprouver ici.
      //
      // Taille déplafonnée : la discussion d'un événement n'est bornée par
      // aucun `limit`, et les 40 Mo par défaut finiraient par évincer des
      // écritures du registre au profit de messages.
      const firestore = initializeFirestore(getApp(), {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
          cacheSizeBytes: CACHE_SIZE_UNLIMITED,
        }),
      });
      if (environment.useEmulators && environment.emulators) {
        connectFirestoreEmulator(
          firestore,
          environment.emulators.firestoreHost,
          environment.emulators.firestorePort
        );
      }
      return firestore;
    }),

    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulators && environment.emulators) {
        connectStorageEmulator(
          storage,
          environment.emulators.storageHost,
          environment.emulators.storagePort
        );
      }
      return storage;
    }),

    provideFunctions(() => {
      const functions = getFunctions();
      if (environment.useEmulators && environment.emulators) {
        // Extract host and port from functionsUrl (e.g. "http://localhost:5001")
        const url = new URL(environment.emulators.functionsUrl);
        connectFunctionsEmulator(functions, url.hostname, parseInt(url.port, 10));
      }
      return functions;
    }),

    // Web FCM (JS SDK) throws `messaging/unsupported-browser` synchronously inside
    // the native WKWebView/Android WebView, which would crash app bootstrap. Only
    // register it on the web; native push goes through @capacitor/push-notifications.
    ...(Capacitor.isNativePlatform() ? [] : [provideMessaging(() => getMessaging())]),
  ],
};
